import { isValidElement, type ReactNode } from 'react';

import { STATUS_MAP, TONE_COLOR, type StatusTone } from './statusTones';

export interface StatusBadgeProps {
  value: string;
  size?: 'sm' | 'lg';
  dot?: boolean;
  children?: ReactNode;
}

function textContent(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textContent).join('');
  if (isValidElement(node)) return textContent((node.props as { children?: ReactNode }).children);
  return '';
}

export function StatusBadge({ value, size = 'sm', dot = true, children }: StatusBadgeProps) {
  const childText = textContent(children).trim();
  const meta = STATUS_MAP[value] ?? { label: childText || value, tone: 'neutral' as StatusTone };
  const t = TONE_COLOR[meta.tone];
  const sz =
    size === 'lg'
      ? { fs: 12, p: '4px 10px', dotS: 6 }
      : { fs: 11, p: '2px 8px', dotS: 5 };
  const label = childText || meta.label;
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: t.bg,
        color: t.fg,
        padding: sz.p,
        borderRadius: 999,
        fontSize: sz.fs,
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && <span aria-hidden="true" style={{ width: sz.dotS, height: sz.dotS, borderRadius: 999, background: t.dot }} />}
      {children ?? meta.label}
    </span>
  );
}
